import React from "react";
import { useTheme } from "react-jss";

import useStyles from "./NavigationBarRenderer.styles";
import { NavigationType } from "../../../models/enums/NavigationType";
import { ThemeModel } from "../../../models/ThemeModel";
import { ShortcutModel } from "../../../models/ShortcutModel";
import Button from "../../shared/Button";
import useTranslatable from "../../../hooks/useTranslatable";
import useDimensionContext from "../../../hooks/useDimensionContext";
import useBlock from "../../../hooks/useBlock";
import { getSettingBoolean } from "../../../models/utilities/settingUtilities";

interface Props {
    surveyTheme: ThemeModel;
    disabled: boolean;
    shortcuts: ShortcutModel[];
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    hideBackButton: boolean;
    onNavigateShortcut(shortcut: ShortcutModel): void;
    onNavigation(type: NavigationType): void;
}

const getButtons = (props: Props, theme: any): NavigationType[] => {
    const buttons: NavigationType[] = [];
    const { hasPreviousPage, hasNextPage, hideBackButton } = props;
    if (hasPreviousPage && !hideBackButton && theme.BackButtonVisible) {
        buttons.push(NavigationType.Back);
    }
    if (hasNextPage && theme.QuitButtonVisible) {
        buttons.push(NavigationType.Quit);
    }
    if (!hasNextPage && theme.SubmitButtonVisible) {
        buttons.push(NavigationType.Submit);
    } else {
        buttons.push(NavigationType.Next);
    }
    return buttons;
};

interface ShortcutProps {
    shortcut: ShortcutModel;
    onClick(): void;
}

const Shortcut = (props: ShortcutProps) => {
    const { onClick, shortcut } = props;

    const text = useTranslatable(shortcut, shortcut.blockName);
    return (
        <Button
            className="navigation__shortcut"
            text={text}
            key={`Shortcut_${shortcut.blockName}`}
            onClick={onClick}
            data-block={shortcut.blockName}
            size="flex"
        />
    );
};

interface NavigationButtonProps {
    disabled?: boolean;
    surveyTheme: ThemeModel;
    types: NavigationType[];
    visibleTypes: NavigationType[];
    onClick(type: NavigationType);
    placeholderWhenHidden?: boolean;
    size: "flex" | "large";
}

function NavigationButton(navButtonProps: NavigationButtonProps) {
    const {
        types,
        disabled,
        surveyTheme,
        onClick,
        visibleTypes,
        placeholderWhenHidden,
        size,
    } = navButtonProps;

    const theme = useTheme();

    const type = types.find((t) => visibleTypes.includes(t)) || types[0];
    const hidden = !visibleTypes.includes(type);

    const dimension = useDimensionContext();
    const isSmallScreen = dimension && dimension.maxWidth(500);
    const themeButtonKey = `${NavigationType[type]}Button`;
    const displayType = theme[`${themeButtonKey}Type`] || "text";
    const icon = theme[`${themeButtonKey}Icon`] ? theme[`${themeButtonKey}Icon`].split(" ") : null;
    const iconOnHover = theme[`${themeButtonKey}IconOnHover`];

    const showText = displayType === "text" || displayType === "textAndIcon";
    const showIcon = displayType === "icon" || displayType === "textAndIcon";

    const className = `navigation__${NavigationType[type].toLowerCase()}`;
    const text = useTranslatable(surveyTheme, `${NavigationType[type]}Button`);

    if (hidden && !placeholderWhenHidden) return null;

    return (
        <Button
            className={className}
            text={text}
            displayText={!isSmallScreen && showText}
            icon={icon}
            displayIcon={showIcon || isSmallScreen}
            iconPosition={type === NavigationType.Back ? "start" : "end"}
            iconOnHover={iconOnHover}
            hidden={hidden}
            disabled={disabled}
            onClick={!hidden ? () => onClick(type) : undefined}
            size={size}
        />
    );
}

export default function NavigationBarRenderer(props: Props) {
    const { shortcuts, onNavigation, onNavigateShortcut, surveyTheme, disabled } = props;

    const theme: any = useTheme();
    const classes = useStyles({ theme });

    const block = useBlock();
    const displayShortcuts = block && getSettingBoolean(block, "DisplayShortcuts");

    const buttons = getButtons(props, theme);

    if (buttons.length === 0) return null;

    const placeholderWhenHidden = theme.BottomNavigationPosition === "wrapper";
    const buttonsSize = theme.BottomNavigationPosition === "floating" ? "flex" : "large";

    return (
        <div className={classes.navigation} role="navigation">
            <div className={classes.navigationButtonContainer}>
                <NavigationButton
                    placeholderWhenHidden={placeholderWhenHidden}
                    disabled={disabled}
                    surveyTheme={surveyTheme}
                    visibleTypes={buttons}
                    onClick={onNavigation}
                    types={[NavigationType.Back]}
                    size={buttonsSize}
                />
                <NavigationButton
                    disabled={disabled}
                    surveyTheme={surveyTheme}
                    visibleTypes={buttons}
                    onClick={onNavigation}
                    types={[NavigationType.Quit]}
                    size={buttonsSize}
                />
                <NavigationButton
                    placeholderWhenHidden={placeholderWhenHidden}
                    disabled={disabled}
                    surveyTheme={surveyTheme}
                    visibleTypes={buttons}
                    onClick={onNavigation}
                    types={[NavigationType.Submit, NavigationType.Next]}
                    size={buttonsSize}
                />
            </div>
            {displayShortcuts && (
                <div className={classes.navigationButtonContainer}>
                    {shortcuts.map((shortcut) => (
                        <Shortcut
                            key={shortcut.blockName}
                            onClick={() => onNavigateShortcut(shortcut)}
                            shortcut={shortcut}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
